// src/components/ExamsSubjectYearPage.jsx
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom'
import { EyeIcon } from '@heroicons/react/20/solid'
import Header from './Header.jsx'
import Footer from './Footer.jsx'
import DocumentMeta from './DocumentMeta.jsx'
import DocumentViewerModal from './DocumentViewerModal.jsx'
import AdUnit from './AdUnit.jsx'
import { trackEvent } from './lib/analytics.jsx'
import { supabase } from '../lib/supabase'
import { MIN_YEAR, THEMATA_YEAR } from './constants.js'

function ExamsSubjectYearPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const backTarget = location.state?.from

  // IMPORTANT: this only works if your router path is /themata/:subjectSlug/:year
  const { subjectSlug, year } = useParams()
  const pageYear = Number(year)
  const safeYear = Number.isFinite(pageYear) ? pageYear : THEMATA_YEAR

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [subject, setSubject] = useState(null)
  const [item, setItem] = useState(null)

  // In-app document viewer
  const [viewerDoc, setViewerDoc] = useState(null)

  const openViewer = (type) => {
    if (!item) return
    const url =
      type === 'exam'
        ? item.exam_url
        : type === 'solution'
        ? item.solution_url
        : item.secondary_url
    if (!url) return

    trackEvent('exam_file_opened', {
      type,
      view: 'modal',
      year: safeYear,
      subject_slug: subjectSlug,
      subject_id: subject?.id || null,
    })

    const titleByType = {
      exam: 'Θέματα',
      solution: 'Λύσεις',
      secondary: item.secondary_label || 'Αρχείο',
    }

    setViewerDoc({
      url,
      kind: type,
      title: item.subject_label,
      subtitle: `${titleByType[type]} • ${safeYear}`,
    })
  }

  const buildPublicUrl = (path) => {
    if (!path) return ''
    const { data } = supabase.storage.from('exams').getPublicUrl(path)
    return data?.publicUrl || ''
  }

  useEffect(() => {
    let cancelled = false

    async function run() {
      setLoading(true)
      setError(null)
      setSubject(null)
      setItem(null)

      // If router mismatch => subjectSlug undefined => redirect
      if (!subjectSlug) {
        navigate(`/themata/${THEMATA_YEAR}`, { replace: true })
        return
      }

      // Year guard
      if (safeYear < MIN_YEAR || safeYear > THEMATA_YEAR) {
        navigate(`/themata/${THEMATA_YEAR}`, { replace: true })
        return
      }

      try {
        // 1) Validate subject exists by slug
        const { data: subj, error: subjErr } = await supabase
          .from('subjects')
          .select('id, name, slug')
          .eq('slug', subjectSlug)
          .maybeSingle()

        if (subjErr) throw subjErr

        if (!subj) {
          navigate(`/themata/${THEMATA_YEAR}`, { replace: true })
          return
        }

        if (cancelled) return
        setSubject(subj)

        // 2) Fetch exam row (NOTE: your DB uses *_path)
        const { data: examRow, error: examErr } = await supabase
          .from('exams')
          .select('id, year, subject_id, exam_path, solution_path, secondary_path, created_on')
          .eq('subject_id', subj.id)
          .eq('year', safeYear)
          .order('created_on', { ascending: true })
          .maybeSingle()

        if (examErr) throw examErr

        // 3) Fetch fields for pills (same approach as ExamsPage)
        const { data: sfRows, error: sfErr } = await supabase
          .from('subject_fields')
          .select('subject_id, fields!inner(number)')
          .eq('subject_id', subj.id)

        if (sfErr) throw sfErr

        const subjectFields = Array.from(
          new Set(
            (sfRows || [])
              .map((r) => r?.fields?.number)
              .filter((n) => typeof n === 'number')
          )
        ).sort((a, b) => a - b)

        if (cancelled) return

        // If subject exists but no exam files for that year
        if (!examRow) {
          setItem(null)
          return
        }

        const getSecondaryLabel = (slug) => {
          if (slug === 'free_design') return 'Οδηγίες'
          if (slug === 'linear_design') return 'Σχέδιο'
          return '' // usually nothing
        }

        setItem({
          subject_label: subj.name,
          subject_slug: subj.slug,
          year: safeYear,
          subject_fields: subjectFields,

          exam_url: buildPublicUrl(examRow.exam_path),
          solution_url: buildPublicUrl(examRow.solution_path),
          secondary_url: buildPublicUrl(examRow.secondary_path),
          secondary_label: getSecondaryLabel(subj.slug),
        })
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Κάτι πήγε στραβά.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [subjectSlug, safeYear, navigate])

  const subjectLabel = subject?.name || subjectSlug || 'Μάθημα'

  const seoTitle = `Θέματα ${subjectLabel} Πανελληνίων ${safeYear} | Πανελλήνιες+`
  const seoDesc = `Κατέβασε τα επίσημα θέματα για ${subjectLabel} (${safeYear}). Βρες επίσης τα θέματα του ίδιου μαθήματος και για άλλα έτη.`

  const otherYears = useMemo(() => {
    const years = []
    for (let y = THEMATA_YEAR; y >= MIN_YEAR; y--) {
      if (y !== safeYear) years.push(y)
    }
    return years
  }, [safeYear])

  return (
    <>
      <DocumentMeta
        title={seoTitle}
        description={seoDesc}
        canonical={`https://www.panellhniesplus.gr/themata/${subjectSlug}/${safeYear}`}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Θέματα',
                item: 'https://www.panellhniesplus.gr/themata/ola',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: `${subjectLabel} ${safeYear}`,
                item: `https://www.panellhniesplus.gr/themata/${subjectSlug}/${safeYear}`,
              },
            ],
          }),
        }}
      />

      <div className="min-h-screen bg-white text-gray-900">
        <Header />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          {/* Top header row */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => (backTarget ? navigate(backTarget) : navigate(-1))}
                  className="cursor-pointer inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50"
                >
                  ← Πίσω
                </button>
              </div>

              <h1 className="mt-4 text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
                {loading ? 'Φόρτωση...' : `Θέματα ${subjectLabel} Πανελληνίων ${safeYear}`}
              </h1>

              <p className="mt-2 text-sm sm:text-base text-gray-600">
                Εδώ θα βρεις τα επίσημα θέματα του μαθήματος{' '}
                <span className="font-semibold">{subjectLabel}</span> στις Πανελλήνιες εξετάσεις του{' '}
                {safeYear}.
              </p>

              {item?.subject_fields?.length ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.subject_fields.map((n) => (
                    <span
                      key={n}
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-200"
                    >
                      Πεδίο {n}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {error ? (
            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
              {error}
            </div>
          ) : null}

          {!loading && subject && !item ? (
            <div className="mt-8 rounded-2xl border border-gray-200 bg-gray-50 p-6">
              <h2 className="text-lg font-extrabold text-gray-900">Δεν βρέθηκαν αρχεία</h2>
              <p className="mt-2 text-sm text-gray-600">
                Δεν έχουμε ανεβάσει ακόμα θέματα για αυτό το μάθημα/έτος.
              </p>
              <Link
                to="/themata/ola"
                className="mt-4 inline-flex items-center justify-center cursor-pointer bg-gray-900 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-800 transition"
              >
                Όλα τα θέματα
              </Link>
            </div>
          ) : null}

          {/* Files card */}
          {item ? (
            <div className="mt-6">
              <ul className="space-y-3">
                <li className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-extrabold text-gray-900 leading-snug">Αρχεία</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {subjectLabel} • {safeYear}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2 sm:justify-end">
                      {item.exam_url && (
                        <button
                          type="button"
                          onClick={() => openViewer('exam')}
                          className="inline-flex items-center justify-center gap-1.5 cursor-pointer bg-gray-900 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-800 transition"
                        >
                          <EyeIcon className="w-4 h-4" />
                          Θέματα
                        </button>
                      )}

                      {item.secondary_url && (
                        <button
                          type="button"
                          onClick={() => openViewer('secondary')}
                          className="inline-flex items-center justify-center gap-1.5 cursor-pointer bg-white text-gray-900 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-300 hover:bg-gray-50 transition"
                        >
                          <EyeIcon className="w-4 h-4" />
                          {item.secondary_label || 'Εναλλακτικό'}
                        </button>
                      )}

                      {item.solution_url && (
                        <button
                          type="button"
                          onClick={() => openViewer('solution')}
                          className="inline-flex items-center justify-center gap-1.5 cursor-pointer bg-white text-gray-900 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-300 hover:bg-gray-50 transition"
                        >
                          <EyeIcon className="w-4 h-4" />
                          Λύσεις
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          ) : null}

          {/* Other years */}
          <section className="mt-8">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h2 className="text-lg font-extrabold text-gray-900">Άλλα έτη για {subjectLabel}</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Δες τα θέματα του ίδιου μαθήματος και για άλλα έτη.
                </p>
              </div>

              <Link
                to="/themata/ola"
                className="hidden sm:inline-flex items-center justify-center cursor-pointer bg-white text-gray-900 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-300 hover:bg-gray-50 transition"
              >
                Όλα τα θέματα
              </Link>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {otherYears.map((y) => (
                <Link
                  key={y}
                  to={`/themata/${subjectSlug}/${y}`}
                  onClick={() => {
                    trackEvent('exam_other_year_clicked', {
                      subject_slug: subjectSlug,
                      from_year: safeYear,
                      to_year: y,
                    })
                  }}
                  className="inline-flex items-center justify-center cursor-pointer px-4 py-2 rounded-xl border border-gray-300 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50"
                >
                  {y}
                </Link>
              ))}
            </div>
          </section>

          <AdUnit />
        </main>

        <Footer />
      </div>

      <DocumentViewerModal
        open={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        url={viewerDoc?.url}
        title={viewerDoc?.title}
        subtitle={viewerDoc?.subtitle}
        kind={viewerDoc?.kind}
      />
    </>
  )
}

export default ExamsSubjectYearPage
